/**
 * Layout maths for the 12-column grid. Pure functions so the builder's drag,
 * resize, keyboard moves and auto-align all agree — and so the rules match
 * the server's: inside 12 columns, no overlaps.
 */

import type { Widget, WidgetType } from './reports-api.ts';

export const COLUMNS = 12;
export const ROW_HEIGHT = 56; // px per grid row on a dashboard

/** Report pages have a fixed shape: rows per page and height/width ratio (mirrors the server). */
export const PAGE_FORMAT: Record<string, { rows: number; ratio: number } | null> = {
  DASHBOARD: null,
  SLIDE_DECK: { rows: 8, ratio: 9 / 16 },
  DOCUMENT: { rows: 17, ratio: 297 / 210 },
};

/** True when every widget sits inside the page (always true for open-ended dashboards). */
export function fitsPage(widgets: Widget[], maxRows: number | null): boolean {
  return maxRows === null || widgets.every((widget) => widget.y + widget.h <= maxRows);
}

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

export const DEFAULT_SIZE: Record<WidgetType, { w: number; h: number }> = {
  METRIC: { w: 3, h: 2 },
  GOAL: { w: 3, h: 2 },
  LINE: { w: 6, h: 4 },
  BAR: { w: 6, h: 4 },
  STACKED_BAR: { w: 6, h: 4 },
  DONUT: { w: 4, h: 4 },
  TABLE: { w: 12, h: 5 },
  TEXT: { w: 6, h: 2 },
  HEADER: { w: 12, h: 1 },
  IMAGE: { w: 4, h: 3 },
  AREA: { w: 6, h: 4 },
  HORIZONTAL_BAR: { w: 6, h: 4 },
  PIE: { w: 4, h: 4 },
  GAUGE: { w: 3, h: 3 },
  SPARKLINE: { w: 4, h: 2 },
  TOC: { w: 6, h: 4 },
  EMBED: { w: 6, h: 4 },
  AI_SUMMARY: { w: 6, h: 3 },
};

export const MIN_SIZE: Record<WidgetType, { w: number; h: number }> = {
  METRIC: { w: 2, h: 2 },
  GOAL: { w: 2, h: 2 },
  LINE: { w: 3, h: 3 },
  BAR: { w: 3, h: 3 },
  STACKED_BAR: { w: 3, h: 3 },
  DONUT: { w: 3, h: 3 },
  TABLE: { w: 4, h: 3 },
  TEXT: { w: 2, h: 1 },
  HEADER: { w: 2, h: 1 },
  IMAGE: { w: 2, h: 2 },
  AREA: { w: 3, h: 3 },
  HORIZONTAL_BAR: { w: 3, h: 3 },
  PIE: { w: 3, h: 3 },
  GAUGE: { w: 2, h: 2 },
  SPARKLINE: { w: 2, h: 2 },
  TOC: { w: 3, h: 2 },
  EMBED: { w: 3, h: 2 },
  AI_SUMMARY: { w: 3, h: 2 },
};

export function overlaps(a: Box, b: Box): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

export function clampBox(box: Box, type: WidgetType): Box {
  const min = MIN_SIZE[type];
  const w = Math.max(min.w, Math.min(COLUMNS, Math.round(box.w)));
  const h = Math.max(min.h, Math.min(40, Math.round(box.h)));
  return {
    x: Math.max(0, Math.min(COLUMNS - w, Math.round(box.x))),
    y: Math.max(0, Math.round(box.y)),
    w,
    h,
  };
}

/**
 * Settle a layout after one widget moved: the moved widget keeps its spot,
 * anything it now overlaps is pushed down, and (with autoAlign) every widget
 * floats up as far as it can — the "auto-align widgets" page setting.
 */
export function settle(widgets: Widget[], movedIndex: number | null, autoAlign: boolean): Widget[] {
  const result = widgets.map((widget) => ({ ...widget }));
  const order = result
    .map((widget, index) => index)
    .sort((a, b) =>
      a === movedIndex
        ? -1
        : b === movedIndex
          ? 1
          : (result[a] as Widget).y - (result[b] as Widget).y ||
            (result[a] as Widget).x - (result[b] as Widget).x,
    );
  const placed: Widget[] = [];
  for (const index of order) {
    const widget = result[index] as Widget;
    if (autoAlign && index !== movedIndex) {
      // A widget floats up into gaps, but never above the section header it sits under.
      const floor = Math.max(
        0,
        ...placed
          .filter(
            (other) => other.type === 'HEADER' && widget.type !== 'HEADER' && other.y < widget.y,
          )
          .map((header) => header.y + header.h),
      );
      while (
        widget.y > floor &&
        !placed.some((other) => overlaps({ ...widget, y: widget.y - 1 }, other))
      )
        widget.y -= 1;
    }
    while (placed.some((other) => overlaps(widget, other))) widget.y += 1;
    placed.push(widget);
  }
  return result;
}

/** First free spot, scanning row by row: where a new widget lands. */
export function firstFreeSpot(
  widgets: Widget[],
  size: { w: number; h: number },
  maxRows: number | null = null,
): Box | null {
  const limit = maxRows === null ? 1000 : maxRows - size.h + 1;
  for (let y = 0; y < limit; y += 1) {
    for (let x = 0; x + size.w <= COLUMNS; x += 1) {
      const box = { x, y, ...size };
      if (!widgets.some((widget) => overlaps(box, widget))) return box;
    }
  }
  return maxRows === null ? { x: 0, y: 1000, ...size } : null;
}

export function gridHeight(widgets: Widget[]): number {
  return widgets.reduce((rows, widget) => Math.max(rows, widget.y + widget.h), 0);
}

/** Mobile preview stacks widgets full width in reading order. */
export function stackForMobile(widgets: Widget[]): Widget[] {
  let y = 0;
  return [...widgets]
    .sort((a, b) => a.y - b.y || a.x - b.x)
    .map((widget) => {
      const stacked = { ...widget, x: 0, w: COLUMNS, y };
      y += widget.type === 'METRIC' || widget.type === 'GOAL' ? 2 : widget.h;
      return stacked;
    });
}
